'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

function IconPhone() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.49 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.4 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6 6l.76-.76a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function FloatingContact() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Suppress on the contact page (overlap with Send Message button) and on
  // every admin route (admin chrome is separate from public site chrome).
  if (pathname === '/contact' || pathname?.startsWith('/admin')) return null;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 12,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Expanded panel */}
      <div
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-hover)',
          borderRadius: 'var(--radius-xl)',
          padding: '20px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          minWidth: 264,
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.97)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          pointerEvents: open ? 'auto' : 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 40px rgba(212,165,116,0.07)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--accent)',
              display: 'block',
              animation: 'status-pulse 2s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.12em',
              color: 'var(--accent)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            OPEN TO WORK
          </span>
        </div>

        <a
          href="sms:8455448757"
          className="float-contact-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(212,165,116,0.06)',
            border: '1px solid rgba(212,165,116,0.22)',
            textDecoration: 'none',
            transition: 'background 0.2s, border-color 0.2s',
          }}
        >
          <span style={{ color: 'var(--accent)', flexShrink: 0 }}>
            <IconPhone />
          </span>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--accent)',
                lineHeight: 1.2,
              }}
            >
              Text (845) 544-8757
            </div>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 2,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.04em',
              }}
            >
              fastest response
            </div>
          </div>
        </a>

        <a
          href="mailto:hello@alejandro.software"
          className="float-contact-row-secondary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border)',
            textDecoration: 'none',
            transition: 'border-color 0.2s',
          }}
        >
          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
            <IconMail />
          </span>
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                lineHeight: 1.2,
              }}
            >
              hello@alejandro.software
            </div>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 2,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.04em',
              }}
            >
              replies within 24h
            </div>
          </div>
        </a>

        <Link
          href="/book"
          style={{
            display: 'block',
            textAlign: 'center',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
            color: 'var(--bg-primary)',
            fontSize: 12,
            fontWeight: 800,
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          Book a call · 30 min →
        </Link>
      </div>

      {/* Toggle button — charcoal/gold ghost so it reads as on-brand, not as a
          third-party chat widget popping into the zen palette. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close contact options' : 'Open contact options'}
        style={{
          width: 54,
          height: 54,
          borderRadius: '50%',
          background: 'var(--bg-elevated)',
          border: open
            ? '1px solid var(--border-hover)'
            : '1px solid var(--accent-mid)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: open ? 'var(--text-secondary)' : 'var(--accent)',
          boxShadow: open
            ? 'none'
            : '0 8px 24px rgba(0,0,0,0.45), 0 0 22px rgba(212,165,116,0.18)',
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}
      >
        {open ? <IconClose /> : <IconChat />}
      </button>
    </div>
  );
}
